/* Dialog de cadastro de operador (ADR-0709). super_admin define e-mail + papel + SENHA INICIAL
   (≥12 chars) — sem convite por e-mail no MVP. Regra de ouro (ADR-0216): DialogContent locked
   enquanto a mutation está em flight (ESC/overlay/X bloqueados). Validação via createStaffBody. */
import { createStaffBody, type StaffRole } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useStaffManagementContent } from '../../content/sections/staff-management.js';
import { useCreateStaff } from '../../hooks/use-staff.js';
import { staffErrorMessage } from './staff-error.js';
import { STAFF_ROLES, staffRoleDescription, staffRoleLabel } from './staff-labels.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

const DEFAULT_ROLE: StaffRole = 'support';

export function CreateStaffDialog({ open, onClose }: Props) {
  const copy = useStaffManagementContent();
  const create = useCreateStaff();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>(DEFAULT_ROLE);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setRole(DEFAULT_ROLE);
      setPassword('');
      setError(null);
    }
  }, [open]);

  /* Validação no submit (molde de FaqFormModal): o botão fica sempre clicável e a mensagem
     de erro é exibida quando o payload não passa no `createStaffBody`. */
  const submit = (): void => {
    setError(null);
    const parsed = createStaffBody.safeParse({ email: email.trim(), role, password });
    if (!parsed.success) {
      setError(copy.createDialog.validationError);
      return;
    }
    create.mutate(parsed.data, {
      onSuccess: (staff) => {
        toast.success(copy.createDialog.success(staff.email));
        onClose();
      },
      onError: (err) => toast.error(staffErrorMessage(err, copy.errors)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !create.isPending) onClose();
      }}
    >
      <DialogContent locked={create.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.createDialog.title}</DialogTitle>
          <DialogDescription>{copy.createDialog.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
          <div className="space-y-1.5">
            <Label htmlFor="staff-email">{copy.createDialog.emailLabel}</Label>
            <Input
              id="staff-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.createDialog.emailPlaceholder}
              disabled={create.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="staff-role">{copy.createDialog.roleLabel}</Label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as StaffRole)}
              disabled={create.isPending}
            >
              <SelectTrigger id="staff-role" aria-label={copy.createDialog.roleLabel}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAFF_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {staffRoleLabel(r, copy.roleLabels)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {staffRoleDescription(role, copy.roleDescriptions)}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="staff-password">{copy.createDialog.passwordLabel}</Label>
            <Input
              id="staff-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={copy.createDialog.passwordPlaceholder}
              disabled={create.isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {copy.createDialog.cancel}
          </Button>
          <Button loading={create.isPending} onClick={submit}>
            {copy.createDialog.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
